import { Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, Users, Building2, Info, Phone, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import Seo, { buildBreadcrumbLd, buildFaqLd, buildLocalServiceLd } from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import PageHero from "../components/PageHero";
import Reveal, { StaggerGroup, itemVariants } from "../components/Reveal";
import FaqAccordion from "../components/FaqAccordion";
import CtaSection from "../components/CtaSection";
import { company } from "../data/site";
import { getLocalPage, localPages } from "../data/localPages";

export default function LocalPage({ slug }) {
  const page = getLocalPage(slug);
  if (!page) return <Navigate to="/einsatzgebiete" replace />;

  const path = `/${page.slug}`;
  const crumbs = [
    { label: "Startseite", to: "/" },
    { label: "Einsatzgebiete", to: "/einsatzgebiete" },
    { label: page.h1, to: path },
  ];
  const others = localPages.filter((p) => p.slug !== page.slug);

  return (
    <div data-testid={`page-local-${page.slug}`}>
      <Seo
        path={path}
        title={page.metaTitle}
        description={page.metaDescription}
        jsonLd={[
          buildBreadcrumbLd(crumbs),
          buildLocalServiceLd({ name: page.city, meta_description: page.metaDescription, state: page.city === "Ulm" ? "Baden-Württemberg" : "Bayern" }, path),
          buildFaqLd(page.faq),
        ]}
      />
      <Breadcrumbs items={crumbs} />
      <PageHero kicker={page.kicker} title={page.h1} lead={page.intro} />

      <section className="border-b border-light-gray bg-white py-6">
        <div className="container-page flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-mid-gray">
          <span className="inline-flex items-center gap-2" data-testid="local-address">
            <MapPin className="h-4 w-4 text-yellow" /> Rümpeltaxi · {company.street}, {company.postal} {company.city} (Iller)
          </span>
          <a href={company.phoneLink} className="inline-flex items-center gap-2 hover:text-anthracite" data-testid="local-phone">
            <Phone className="h-4 w-4 text-yellow" /> {company.phoneDisplay}
          </a>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="container-page grid gap-14 lg:grid-cols-[1.3fr_0.7fr] lg:gap-20">
          <Reveal>
            <div className="space-y-4" data-testid="local-body">
              {page.paragraphs.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-mid-gray">
                  {p}
                </p>
              ))}
            </div>

            <h2 className="mt-10 font-heading text-2xl font-bold text-anthracite md:text-3xl">
              Ihr Einsatz in {page.city}
            </h2>
            <StaggerGroup className="mt-6 space-y-3">
              {page.highlights.map((h) => (
                <motion.div key={h} variants={itemVariants} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-yellow text-brand-black">
                    <Check className="h-4 w-4" />
                  </span>
                  <span className="text-base text-mid-gray">{h}</span>
                </motion.div>
              ))}
            </StaggerGroup>

            <div className="mt-8 flex items-start gap-3 rounded-sm bg-light-gray p-5">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-mid-gray" />
              <p className="text-sm text-mid-gray">
                Rümpeltaxi ist ein Betrieb aus Altenstadt an der Iller (Bayern) mit Inhaber Thomas
                Dietrich. Wir sind in {page.city} und der Region regelmäßig im Einsatz und stimmen jeden
                Termin individuell mit Ihnen ab. Anfrage über das{" "}
                <Link to="/kontakt" className="font-semibold text-anthracite underline hover:text-yellow-dark">
                  Kontaktformular
                </Link>{" "}
                oder telefonisch unter{" "}
                <a href={company.phoneLink} className="font-semibold text-anthracite underline hover:text-yellow-dark">
                  {company.phoneDisplay}
                </a>
                .
              </p>
            </div>
          </Reveal>

          <Reveal delay={1}>
            <div className="grid gap-4">
              <a
                href={company.phoneLink}
                className="group flex items-center justify-between rounded-sm bg-yellow p-6 transition-colors hover:bg-yellow-dark"
                data-testid="local-call"
              >
                <span className="flex items-center gap-3">
                  <Phone className="h-6 w-6 text-brand-black" />
                  <span className="font-heading font-bold text-brand-black">{company.phoneDisplay}</span>
                </span>
                <ArrowRight className="h-5 w-5 text-brand-black transition-transform group-hover:translate-x-1" />
              </a>
              <Link
                to="/kontakt"
                className="group flex items-center justify-between rounded-sm border border-light-gray bg-light-gray p-6 transition-colors hover:border-yellow hover:bg-white"
                data-testid="local-contact"
              >
                <span className="font-heading font-bold text-anthracite">Unverbindlich anfragen</span>
                <ArrowRight className="h-5 w-5 text-mid-gray transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/privatkunden"
                className="group flex items-center justify-between rounded-sm border border-light-gray bg-light-gray p-6 transition-colors hover:border-yellow hover:bg-white"
                data-testid="local-private"
              >
                <span className="flex items-center gap-3">
                  <Users className="h-6 w-6 text-yellow" />
                  <span className="font-heading font-bold text-anthracite">Für Privatkunden</span>
                </span>
                <ArrowRight className="h-5 w-5 text-mid-gray transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/geschaeftskunden"
                className="group flex items-center justify-between rounded-sm border border-light-gray bg-light-gray p-6 transition-colors hover:border-yellow hover:bg-white"
                data-testid="local-business"
              >
                <span className="flex items-center gap-3">
                  <Building2 className="h-6 w-6 text-yellow" />
                  <span className="font-heading font-bold text-anthracite">Für Geschäftskunden</span>
                </span>
                <ArrowRight className="h-5 w-5 text-mid-gray transition-transform group-hover:translate-x-1" />
              </Link>
              <div className="rounded-sm border border-light-gray bg-white p-6">
                <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-mid-gray">
                  Weitere Orte
                </h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {others.map((o) => (
                    <Link
                      key={o.slug}
                      to={`/${o.slug}`}
                      className="rounded-sm bg-light-gray px-3 py-1.5 text-xs text-anthracite transition-colors hover:bg-yellow"
                      data-testid={`local-link-${o.slug}`}
                    >
                      {o.h1}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-light-gray py-16 md:py-24">
        <div className="container-page grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          <Reveal>
            <span className="kicker">FAQ</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl">
              Fragen zu {page.city}
            </h2>
          </Reveal>
          <Reveal delay={1}>
            <FaqAccordion items={page.faq} idPrefix={page.slug} />
          </Reveal>
        </div>
      </section>

      <CtaSection title={`${page.h1} anfragen`} />
    </div>
  );
}
